import type { PropertyIntent } from '@/types/property'

export type PropertyEventParams = Record<string, string | number | boolean | undefined>

const MIN_ACTIVE_MS = 3000
const MAX_ACTIVE_SECONDS = 1800
const VIEW_DEDUPE_MS = 1500

export function propertyEventParams(input: {
  propertyId?: number | null
  cityId?: number | null
  areaId?: number | null
  categoryId?: number | null
  intent?: string
  source?: string
  listingPosition?: number | null
  durationSeconds?: number | null
}): PropertyEventParams {
  return {
    property_id: positiveId(input.propertyId),
    city_id: positiveId(input.cityId),
    area_id: positiveId(input.areaId),
    category_id: positiveId(input.categoryId),
    intent: knownIntent(input.intent),
    source: input.source,
    listing_position: positivePosition(input.listingPosition),
    duration_seconds: input.durationSeconds != null && input.durationSeconds > 0
      ? input.durationSeconds
      : undefined,
  }
}

export function claimPropertyImpression(seen: Set<string>, propertyId: number): boolean {
  if (!Number.isFinite(propertyId) || propertyId <= 0) return false
  const key = String(propertyId)
  if (seen.has(key)) return false
  seen.add(key)
  return true
}

/** Same result set ignores a property already claimed. A new key starts a new set. */
export function impressionResultSets() {
  const sets = new Map<string, Set<string>>()
  return {
    claim(resultSetKey: string, propertyId: number): boolean {
      const seen = sets.get(resultSetKey) ?? new Set<string>()
      sets.set(resultSetKey, seen)
      return claimPropertyImpression(seen, propertyId)
    },
  }
}

export function shouldLogPropertyView(input: {
  previousId?: number | null
  previousAtMs?: number | null
  propertyId: number
  nowMs: number
}): boolean {
  if (!Number.isFinite(input.propertyId) || input.propertyId <= 0) return false
  if (
    input.previousId === input.propertyId &&
    input.previousAtMs != null &&
    input.nowMs - input.previousAtMs < VIEW_DEDUPE_MS
  ) {
    return false
  }
  return true
}

/** Repeat is another property_view for the same id, not a second event name. */
export function isRepeatPropertyView(viewCount: number): boolean {
  return viewCount >= 2
}

export function shouldLogPropertySaved(input: {
  requestSucceeded: boolean
  likedAfter: boolean
}): boolean {
  return input.requestSucceeded && input.likedAfter
}

export function galleryActionEvent(action: 'render' | 'swipe' | 'open'): 'gallery_open' | null {
  return action === 'open' ? 'gallery_open' : null
}

export function propertySurfaceEvent(
  surface: 'card_render' | 'card_tap' | 'detail_ready' | 'detail_rerender',
): 'property_click' | 'property_view' | null {
  if (surface === 'card_tap') return 'property_click'
  if (surface === 'detail_ready') return 'property_view'
  return null
}

/** Visible time only. Drops short passes and caps a long stay. */
export function activeDurationSeconds(elapsedMs: number): number | null {
  if (!Number.isFinite(elapsedMs) || elapsedMs < MIN_ACTIVE_MS) return null
  const seconds = Math.floor(elapsedMs / 1000)
  return Math.min(seconds, MAX_ACTIVE_SECONDS)
}

function positiveId(value: number | null | undefined): number | undefined {
  return value != null && Number.isFinite(value) && value > 0 ? value : undefined
}

function positivePosition(value: number | null | undefined): number | undefined {
  return value != null && Number.isInteger(value) && value > 0 ? value : undefined
}

function knownIntent(value: string | undefined): PropertyIntent | undefined {
  if (value === 'buy' || value === 'rent' || value === 'commercial') return value
  return undefined
}
